/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import useRouter from 'js/lib/useRouter';

import { Button, Typography, Typography2, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { TextTruncate, css as cuicss, placeholder } from '@coursera/coursera-ui';
import type { SectionName } from '@coursera/event-pulse-types';

import { CMLUtils } from 'bundles/cml';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { getAllProducts } from 'bundles/enterprise-collections/components/getCollectionItemList';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';
import { toProgramSkillSet } from 'bundles/program-common/links';
import type { CollectionListItemStruct } from 'bundles/program-common/types/programCommon';
import type { ObjectiveListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements as TargetSkillProficiencySummary } from 'bundles/program-home/components/__generated__/ObjectiveListQuery';
import {
  ProductInfoList,
  ProductInfoListPlaceholder,
} from 'bundles/program-home/components/skillSetProductCollectionCard/ProductInfoList';
import {
  SkillPillList,
  SkillPillListPlaceholder,
} from 'bundles/program-home/components/skillSetProductCollectionCard/SkillPillList';
import { GoldStarSvg } from 'bundles/skills-common';

import _t from 'i18n!nls/program-home';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type SkillSetProductCollectionCardProp = {
  skillSet: TargetSkillProficiencySummary;
  programSlug: string;
  collectionListItemStruct: CollectionListItemStruct;
  skillSetOrder: number;
  filterTrackingData: string[];
};

const styles = {
  container: (theme: Theme) => css`
    border: 1px solid ${theme.palette.gray[300]};
    box-sizing: border-box;
    box-shadow: 0 4px 4px rgb(0 0 0 / 10%);
    border-radius: 2px;
    overflow: hidden;
    height: 100%;
  `,
  headerContainer: (theme: Theme) => css`
    background-color: ${theme.palette.purple[800]};
  `,
  innerContainer: (theme: Theme) => css`
    padding: ${theme.spacing(16)} ${theme.spacing(24)};
    ${theme.breakpoints.down('sm')} {
      padding: ${theme.spacing(16)};
    }
  `,
  detailsContainer: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    height: calc(100% - 244px);
    ${theme.breakpoints.down('sm')} {
      height: calc(100% - 236px);
    }
    ${theme.breakpoints.down('xs')} {
      height: calc(100% - 228px);
    }
  `,
  headerTitle: css`
    display: flex;
    align-items: center;
  `,
  skillSetHeader: (theme: Theme) => css`
    margin-left: ${theme.spacing(8)};
  `,
  skillSetName: (theme: Theme) => css`
    margin: ${theme.spacing(16)} 0;
    ${theme.breakpoints.down('sm')} {
      margin: ${theme.spacing(12)} 0;
    }
    ${theme.breakpoints.down('xs')} {
      margin: ${theme.spacing(8)} 0;
    }

    min-height: 72px;
    display: flex;
    align-items: center;

    > .name-container {
      flex-grow: 1;
    }
  `,
  description: css`
    min-height: 72px;
  `,
  gainMessageContainer: (theme: Theme) => css`
    margin: ${theme.spacing(8)} 0;
    ${theme.breakpoints.down('sm')} {
      margin-top: 0;
    }
  `,
  linkSection: (theme: Theme) => css`
    margin-top: auto;
    margin-left: calc(${theme.spacing(16)} * -1);
    align-self: flex-start;
    ${theme.breakpoints.down('sm')} {
      margin-left: calc(${theme.spacing(8)} * -1);
    }
  `,
};

export function SkillSetProductCollectionCardPlaceholder() {
  const theme = useTheme();
  return (
    <div css={styles.container(theme)} aria-hidden data-testId="SkillSetProductCollectionCardPlaceholder">
      <div css={[styles.headerContainer(theme), styles.innerContainer(theme)]} aria-hidden>
        <div css={styles.headerTitle} aria-hidden>
          <GoldStarSvg width={31} height={36} />
          <Typography2
            display="inline"
            css={styles.skillSetHeader(theme)}
            component="div"
            variant="subtitleMedium"
            color="invertBody"
            aria-hidden
          >
            {_t('SKILLSET')}
          </Typography2>
        </div>
        <Typography
          css={styles.skillSetName(theme)}
          variant="h1semibold"
          component="div"
          {...cuicss(placeholder.styles.shimmer)}
          color="invertBody"
          aria-hidden
        >
          &nbsp;
        </Typography>
        <Typography
          {...cuicss(placeholder.styles.shimmer)}
          css={styles.description}
          component="div"
          color="invertBody"
          aria-hidden
        >
          &nbsp;
        </Typography>
      </div>
      <div css={[styles.innerContainer(theme), styles.detailsContainer(theme)]}>
        <Typography {...cuicss(placeholder.styles.shimmer)} variant="h3semibold" component="div" aria-hidden>
          &nbsp;
        </Typography>
        <SkillPillListPlaceholder />
        <Typography2 variant="subtitleMedium" component="div" css={styles.gainMessageContainer(theme)} aria-hidden>
          {_t('Grow your skills with recommendations:')}
        </Typography2>
        <ProductInfoListPlaceholder />
        <Button
          aria-hidden
          css={styles.linkSection(theme)}
          variant="ghost"
          disabled={true}
          icon={<ArrowNextIcon size="medium" />}
          iconPosition="after"
        >
          {_t('View all recommendations')}
        </Button>
      </div>
    </div>
  );
}

/**
 * This component displays the CDS button but makes it non interactive from a keyboard or screenreader
 * Click events bubble up to the card container which is interactive
 * Full context here: https://coursera.atlassian.net/browse/ACCESS-2497?focusedCommentId=586399
 */
const NonInteractiveButton = (props: ButtonProps) => (
  <>
    {/* not all button props are compatible with div. Specifically interactive props. But we disable events for this div anyway */}
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-expect-error */}
    <div {...props} role="presentation" aria-hidden tabIndex={-1}>
      {props.children}
    </div>
  </>
);

/**
 * Note on tracking:
 * Only the container is clickable or focusable in this component to avoid an A11y error for nested interactive elements.
 * However we still want to track clicks to different areas of the component. The skillset container will also register a click
 * in these instances but we can also subtract the subclass click count from the container click count to get the accurate count.
 */
export function SkillSetProductCollectionCard({
  skillSet,
  programSlug,
  collectionListItemStruct,
  skillSetOrder,
  filterTrackingData,
}: SkillSetProductCollectionCardProp) {
  const theme = useTheme();
  const track = useTracker();

  const { id, description, targetSkillProficiencies, title, slug } = skillSet;
  const products = getAllProducts(collectionListItemStruct);
  const router = useRouter();

  const eventingV3SkillSetData = {
    skillsetCard: {
      index: skillSetOrder,
    },
    pageSection: {
      sectionName: 'cta_banner' as SectionName,
      index: 1,
    },
    filter: filterTrackingData,
    skillset: {
      id,
      name: title,
    },
  };

  const href = toProgramSkillSet(programSlug, slug);
  const onSkillSetContainerClick = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      track('click_skillset_card', eventingV3SkillSetData);
      redirectClientOrServer(href);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, href]
  );

  const skillsetCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_skillset_card',
    eventingV3SkillSetData
  );

  return (
    <div ref={skillsetCardRef}>
      <TrackedDiv
        trackingName="skillset_container"
        data={{
          skillSetOrder,
          objectId: `skillset~${programSlug}/${skillSet.slug}`,
          skillsetSlug: skillSet.slug,
          programSlug,
          tab: 'skillset-tab',
        }}
        css={styles.container(theme)}
        role="link"
        aria-label={_t('Skillset: #{title}', { title })}
        onClick={onSkillSetContainerClick}
        onKeyPress={(event) => a11yKeyPress(event, onSkillSetContainerClick)}
        data-e2e="ObjectiveCard"
        trackClicks
        withVisibilityTracking
        data-testId="skillset-container"
      >
        <div css={[styles.headerContainer(theme), styles.innerContainer(theme)]}>
          <div css={styles.headerTitle}>
            <GoldStarSvg width={31} height={36} />
            <Typography2
              display="inline"
              css={styles.skillSetHeader(theme)}
              component="div"
              variant="subtitleMedium"
              color="invertBody"
            >
              {_t('SKILLSET')}
            </Typography2>
          </div>
          <Typography css={styles.skillSetName(theme)} component="h2" variant="h1semibold" color="invertBody">
            {title}
          </Typography>
          <Typography2 css={styles.description} component="div" color="invertBody">
            {description?.cml && (
              <TextTruncate
                line={3}
                text={CMLUtils.getInnerText(CMLUtils.create(description.cml.value, description.cml.dtdId))}
                showTextBeforeTruncation={false}
              />
            )}
          </Typography2>
        </div>
        <div css={[styles.innerContainer(theme), styles.detailsContainer(theme)]}>
          <Typography2 variant="subtitleMedium" component="h3">
            <FormattedMessage
              message={_t('Gain {numSkills, plural, =1 {# skill} other {# skills}}:')}
              numSkills={targetSkillProficiencies.length}
            />
          </Typography2>
          <SkillPillList targetSkillProficiencies={targetSkillProficiencies} />

          <Typography2 css={styles.gainMessageContainer(theme)} component="h3" variant="subtitleMedium">
            {_t('Grow your skills with recommendations:')}
          </Typography2>
          <ProductInfoList
            products={products}
            programSlug={programSlug}
            skillsetSlug={skillSet.slug}
            skillSetOrder={skillSetOrder}
          />
          <TrackedButton
            css={styles.linkSection(theme)}
            component={NonInteractiveButton}
            variant="ghost"
            trackingName="skillset_link"
            trackingData={{
              skillSetOrder,
              objectId: `skillset~${programSlug}/${skillSet.slug}`,
              skillsetSlug: skillSet.slug,
              programSlug,
              tab: 'skillset-tab',
            }}
            icon={<ArrowNextIcon size="medium" />}
            iconPosition="after"
            data-testId="view-recommendation-presentational-link"
          >
            {_t('View all recommendations')}
          </TrackedButton>
        </div>
      </TrackedDiv>
    </div>
  );
}
